/**
 * Reading text from a photograph of a medicine label.
 *
 * The photograph is processed on this device and is never uploaded or stored. Recognition
 * runs against assets served from TED's own origin, so a picture of a prescription never
 * reaches a third party.
 *
 * Recognition fails badly rather than obviously: a photograph taken at an angle, or in poor
 * light, produces confident-looking nonsense rather than an error. Measured on a synthetic
 * label, a flat and sharp photograph scores around 90% and yields every field, while an
 * angled and slightly blurred one collapses to near zero and yields gibberish. So a result
 * is only offered once it clears a confidence floor, and TED never decides which line is the
 * medicine's name — it offers the lines and the user chooses.
 */

/** Below this, the text is unreliable often enough that offering it would be misleading. */
const MINIMUM_OVERALL_CONFIDENCE = 55;
const MINIMUM_LINE_CONFIDENCE = 60;

export type ScanCandidate = { text: string; confidence: number };
export type ScanReading =
  | { ok: true; lines: ScanCandidate[] }
  | { ok: false; reason: string };

export type RecognisedText = { confidence: number; lines: { text: string; confidence: number }[] };

const tidy = (value: string) => value.replace(/\s+/g, " ").trim();

/**
 * Turn a recognition result into lines worth offering, or an explanation of why none are.
 * Kept separate from recognition itself so the judgement can be tested without a camera.
 */
export function readLabelLines(result: RecognisedText): ScanReading {
  if (!Number.isFinite(result.confidence) || result.confidence < MINIMUM_OVERALL_CONFIDENCE) {
    return { ok: false, reason: "That photo was not clear enough to read. Lay the label flat, fill the frame, and try again in better light." };
  }
  const seen = new Set<string>();
  const lines: ScanCandidate[] = [];
  for (const line of result.lines ?? []) {
    const text = tidy(line.text ?? "");
    // Single characters are almost always noise, and a low-scoring line among good ones is
    // usually a misread rather than something the label actually says.
    if (text.length < 2 || line.confidence < MINIMUM_LINE_CONFIDENCE) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    lines.push({ text, confidence: line.confidence });
  }
  if (lines.length === 0) {
    return { ok: false, reason: "Nothing readable was found in that photo. Lay the label flat, fill the frame, and try again in better light." };
  }
  return { ok: true, lines };
}

/* --------------------------------------------------------------------------------------
 * Reading fields out of the lines.
 *
 * Everything below proposes only what is written on the label. TED knows nothing about any
 * medicine and must never supply a detail the label does not carry: no typical dose, no
 * usual strength, no assumed form. Each proposal names the line it came from so it can be
 * checked against the label, and none is applied without being approved.
 * ----------------------------------------------------------------------------------- */

export type LabelField = "name" | "strength" | "form" | "amount" | "frequency";

export type LabelProposal = {
  field: LabelField;
  label: string;
  /** What will be shown for approval. */
  display: string;
  /** The line this was read from, so the user can check it. */
  source: string;
  /** A caveat where the label does not actually carry the whole answer. */
  caveat?: string;
  value: {
    name?: string;
    strength?: string;
    strengthUnit?: string;
    form?: string;
    amount?: string;
    timesPerDay?: number;
    everyHours?: number;
  };
};

const NUMBER_WORDS: Record<string, number> = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  once: 1, twice: 2, thrice: 3,
};

/** Words a label uses for a form, mapped onto the forms TED already stores. */
const FORM_WORDS: [RegExp, string][] = [
  [/\btablets?\b/i, "tablet"],
  [/\bcaps(ules?)?\b/i, "capsule"],
  [/\b(injections?|pre-?filled syringes?)\b/i, "injection"],
  [/\b(oral )?(liquid|suspension|solution|syrup|elixir)\b/i, "liquid"],
  [/\bpatch(es)?\b/i, "patch"],
  [/\b(inhaler|puffs?)\b/i, "inhaler"],
  [/\bsprays?\b/i, "spray"],
  [/\bdrops?\b/i, "drops"],
  [/\bsuppositor(y|ies)\b/i, "suppository"],
  [/\b(cream|ointment|gel)\b/i, "cream-ointment"],
];

const STRENGTH = /(\d+(?:[.,]\d+)?)\s*(micrograms?|mcg|mg|grams?|g|ml|millilitres?|units?)\b/i;
/** Combined strengths such as "30mg/500mg" must be removed whole, not just the first part. */
const ALL_STRENGTHS = new RegExp(STRENGTH.source, "gi");
const INSTRUCTION = /\b(take|use|apply|inject|inhale|swallow|insert)\b/i;
/** Lines that are dispensing furniture rather than anything about the medicine. */
const BOILERPLATE = /\b(keep out of reach|pharmac|quantity|qty|exp(iry)?|batch|lot no|dispensed|patient|date|warning|store below|read the (leaflet|label)|not a real medicine|synthetic)\b/i;

const normaliseUnit = (unit: string) => {
  const lower = unit.toLowerCase();
  if (lower.startsWith("microgram") || lower === "mcg") return "mcg";
  if (lower === "mg") return "mg";
  if (lower.startsWith("gram") || lower === "g") return "g";
  if (lower === "ml" || lower.startsWith("millilitre")) return "mL";
  return "units";
};

const wordOrDigit = (token: string) => /^\d+(?:\.\d+)?$/.test(token) ? Number(token) : NUMBER_WORDS[token.toLowerCase()];

/**
 * Read what the label states into proposals for the medicine form.
 * Returns only fields the text actually supports; anything absent is simply not proposed.
 */
export function proposeFromLabel(lines: string[]): LabelProposal[] {
  const proposals: LabelProposal[] = [];
  const clean = lines.map(tidy).filter(Boolean);

  // Strength, and the line carrying it, which is usually the line naming the medicine.
  const strengthLine = clean.find((line) => STRENGTH.test(line) && !INSTRUCTION.test(line));
  const strengthMatch = strengthLine?.match(STRENGTH);
  if (strengthLine && strengthMatch) {
    proposals.push({
      field: "strength", label: "Strength", display: `${strengthMatch[1].replace(",", ".")} ${normaliseUnit(strengthMatch[2])}`,
      source: strengthLine,
      value: { strength: strengthMatch[1].replace(",", "."), strengthUnit: normaliseUnit(strengthMatch[2]) },
    });
  }

  // Name: the strength line with the strength and any form word taken out. Nothing is added.
  const nameSource = strengthLine ?? clean.find((line) => !INSTRUCTION.test(line) && !BOILERPLATE.test(line) && line.length > 3);
  if (nameSource) {
    let name = nameSource.replace(ALL_STRENGTHS, " ");
    for (const [pattern] of FORM_WORDS) name = name.replace(pattern, " ");
    // A combined strength leaves its separator behind once both parts are gone.
    name = tidy(name.replace(/\s*[\/+]\s*/g, " ").replace(/[.,;:\-\/]+$/, "").replace(/\s{2,}/g, " "));
    if (name.length >= 2) {
      proposals.push({ field: "name", label: "Medicine name", display: name, source: nameSource, value: { name } });
    }
  }

  // Form, from anywhere on the label.
  for (const [pattern, form] of FORM_WORDS) {
    const line = clean.find((entry) => pattern.test(entry));
    if (!line) continue;
    proposals.push({ field: "form", label: "Form", display: form === "cream-ointment" ? "Cream / ointment" : form.charAt(0).toUpperCase() + form.slice(1), source: line, value: { form } });
    break;
  }

  // How many to take, from the instruction line only.
  const instruction = clean.find((line) => INSTRUCTION.test(line));
  if (instruction) {
    const after = instruction.replace(/^.*?\b(take|use|apply|inject|inhale|swallow|insert)\b/i, "");
    const token = after.split(/\s+/).map((word) => word.replace(/[^\w.]/g, "")).find((word) => word && wordOrDigit(word) !== undefined);
    const amount = token ? wordOrDigit(token) : undefined;
    if (amount !== undefined && amount > 0) {
      proposals.push({ field: "amount", label: "Amount each time", display: String(amount), source: instruction, value: { amount: String(amount) } });
    }

    // How often. Only patterns the label states outright.
    const everyHours = instruction.match(/every\s+(\d+|[a-z]+)\s*(?:hours?|hrs?)\b/i);
    const timesADay = instruction.match(/\b(once|twice|thrice|\d+|[a-z]+)\s*(?:times?\s*)?(?:a|per|each)?\s*day\b/i)
      ?? instruction.match(/\b(once|twice|thrice|\d+|[a-z]+)\s*times?\s+daily\b/i)
      ?? instruction.match(/\b(once|twice|thrice)\s+daily\b/i);
    const hours = everyHours ? wordOrDigit(everyHours[1]) : undefined;
    const times = timesADay ? wordOrDigit(timesADay[1]) : undefined;
    if (hours !== undefined && hours > 0) {
      proposals.push({
        field: "frequency", label: "How often", display: `Every ${hours} hours`, source: instruction,
        value: { everyHours: hours },
      });
    } else if (times !== undefined && times > 0 && times <= 12) {
      proposals.push({
        field: "frequency", label: "How often", display: `${times} ${times === 1 ? "time" : "times"} a day`, source: instruction,
        caveat: "The label does not say at which times. TED will add this many slots with placeholder times for you to set.",
        value: { timesPerDay: times },
      });
    }
  }

  const order: LabelField[] = ["name", "strength", "form", "amount", "frequency"];
  return proposals.sort((a, b) => order.indexOf(a.field) - order.indexOf(b.field));
}

let scanAvailable = false;
let scanCheck: Promise<boolean> | undefined;

/**
 * Whether the recognition assets can be reached.
 *
 * Only a positive answer is remembered. A failure here almost always means there is no
 * connection yet, not a device that cannot scan, and caching that would hide the button
 * for the rest of the session — on an installed PWA, until the app is force-quit.
 */
export async function labelScanningAvailable() {
  if (scanAvailable) return true;
  // Share one in-flight check, so several callers do not each ask the network.
  scanCheck ??= (async () => {
    try {
      const response = await fetch("/ocr/eng.traineddata.gz", { method: "HEAD" });
      if (response.ok) scanAvailable = true;
      return response.ok;
    } catch {
      return false;
    } finally {
      scanCheck = undefined;
    }
  })();
  return scanCheck;
}

/**
 * Read a photograph. The recognition engine is roughly 9 MB and is loaded only here, on
 * first use, so it never forms part of the offline shell.
 */
export async function readLabel(image: File, onProgress?: (fraction: number) => void): Promise<ScanReading> {
  try {
    const { createWorker } = await import("tesseract.js");
    const worker = await createWorker("eng", 1, {
      workerPath: "/ocr/worker.min.js",
      corePath: "/ocr/tesseract-core-simd-lstm.wasm.js",
      langPath: "/ocr",
      gzip: true,
      logger: onProgress ? (message: { status?: string; progress?: number }) => {
        if (message.status === "recognizing text" && typeof message.progress === "number") onProgress(message.progress);
      } : undefined,
    });
    try {
      // Per-line detail is omitted unless asked for, and the line confidences are what
      // separate a usable reading from confident-looking noise.
      const { data } = await worker.recognize(image, {}, { blocks: true, text: true });
      const lines = (data.blocks ?? []).flatMap((block) => block.paragraphs ?? []).flatMap((paragraph) => paragraph.lines ?? []);
      return readLabelLines({ confidence: data.confidence, lines: lines.map((line) => ({ text: line.text, confidence: line.confidence })) });
    } finally {
      await worker.terminate();
    }
  } catch {
    return { ok: false, reason: "Text recognition could not start on this device. Enter the details by hand instead." };
  }
}
